import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HCaptcha } from '../components/HCaptcha';

interface ForgotPasswordPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({ showToast }) => {
  const { resetPassword } = useAuth();
  
  const [email, setEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      showToast('Veuillez entrer votre adresse email', 'error');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showToast('Veuillez entrer une adresse email valide', 'error');
      return;
    }

    if (!captchaToken) {
      showToast('Veuillez valider le captcha de sécurité', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // ✅ CORRECTION: Utiliser resetPasswordForEmail avec captchaToken
      // Ceci envoie un lien de RÉCUPÉRATION (type=recovery) vers /auth/callback
      const { error } = await resetPassword(email, captchaToken);

      if (error) {
        
        // Messages d'erreur en français
        let errorMessage = 'Erreur lors de l\'envoi de l\'email';
        
        switch (error.message) {
          case 'User not found':
            errorMessage = 'Aucun compte n\'est associé à cette adresse email';
            break;
          case 'Email rate limit exceeded':
            errorMessage = 'Trop de demandes. Veuillez réessayer dans quelques minutes';
            break;
          case 'For security purposes, you can only request this once every 60 seconds':
            errorMessage = 'Veuillez attendre 60 secondes avant de renvoyer un email';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
        
        showToast(errorMessage, 'error');
        return;
      }

      setEmailSent(true);
      setCaptchaToken(''); // Réinitialiser le captcha après succès
      showToast('Lien de récupération envoyé ! Vérifiez votre email.', 'success');
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-green-600 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-check text-white text-2xl"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Email envoyé !
            </h2>
            <p className="text-gray-600">
              Nous avons envoyé un lien de récupération à votre adresse email
            </p>
          </div>

          {/* Contenu */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <i className="fas fa-envelope text-blue-600 mr-2"></i>
                  Email envoyé à : <strong>{email}</strong>
                </p>
              </div>

              <div className="text-left space-y-3 text-sm text-gray-600">
                <h3 className="font-semibold text-gray-900 text-center mb-3">
                  Prochaines étapes :
                </h3>
                <div className="space-y-2">
                  <p className="flex items-start">
                    <span className="bg-[#002c46] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5 flex-shrink-0">1</span>
                    Vérifiez votre boîte mail (et le dossier spam)
                  </p>
                  <p className="flex items-start">
                    <span className="bg-[#002c46] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5 flex-shrink-0">2</span>
                    Cliquez sur le lien dans l'email reçu
                  </p>
                  <p className="flex items-start">
                    <span className="bg-[#002c46] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5 flex-shrink-0">3</span>
                    Vous serez redirigé vers la page de changement de mot de passe
                  </p>
                </div>
              </div>

              <div className="pt-4 space-y-3">
                <button
                  onClick={() => {
                    setEmailSent(false);
                    setEmail('');
                  }}
                  className="w-full py-3 px-4 border border-[#002c46] text-[#002c46] font-semibold rounded-lg hover:bg-[#002c46]/5 transition-colors"
                >
                  <i className="fas fa-redo mr-2"></i>
                  Renvoyer l'email
                </button>
                
                <Link
                  to="/login"
                  className="block w-full py-3 px-4 bg-[#002c46] text-white font-semibold rounded-lg hover:bg-[#001a2a] transition-colors text-center"
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Retour à la connexion
                </Link>
              </div>
            </div>
          </div>

          {/* Support */}
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">
              Vous ne recevez pas l'email ?
            </p>
            <Link
              to="/contact"
              className="text-[#002c46] hover:text-[#001a2a] font-medium text-sm"
            >
              <i className="fas fa-envelope mr-1"></i>
              Contactez-nous
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-[#002c46] rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-key text-white text-2xl"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Mot de passe oublié ?
          </h2>
          <p className="text-gray-600">
            Entrez votre adresse email pour recevoir un lien de réinitialisation
          </p>
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-envelope text-[#002c46] mr-2"></i>
                Adresse email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                placeholder="votre.email@exemple.com"
                disabled={isSubmitting}
              />
            </div>

            {/* Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <i className="fas fa-info-circle text-blue-600 mr-2"></i>
                Nous vous enverrons un lien sécurisé pour réinitialiser votre mot de passe. Le lien expirera après une heure.
              </p>
            </div>

            {/* hCaptcha - Protection anti-bot */}
            <div className="space-y-3">
              <div className={`p-4 rounded-lg border-2 transition-all ${
                captchaToken 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-300 bg-gray-50'
              }`}>
                {!captchaToken && (
                  <HCaptcha
                    onVerify={(token) => {
                      setCaptchaToken(token);
                    }}
                    onExpire={() => {
                      setCaptchaToken('');
                    }}
                    onError={() => {
                      setCaptchaToken('');
                      showToast('Erreur de vérification. Veuillez réessayer.', 'error');
                    }}
                  />
                )}
                
                {captchaToken ? (
                  <div className="text-center py-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-3">
                      <i className="fas fa-check text-white text-3xl"></i>
                    </div>
                    <p className="text-lg font-bold text-green-700 mb-1">
                      ✅ Vérification réussie !
                    </p>
                    <p className="text-sm text-green-600">
                      Le bouton est maintenant activé
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-600 text-center mt-2">
                    <i className="fas fa-shield-alt mr-1"></i>
                    Protection contre les robots automatiques
                  </p>
                )}
              </div>
            </div>

            {/* Bouton d'envoi */}
            <button
              type="submit"
              disabled={isSubmitting || !captchaToken}
              className="w-full py-3 px-4 bg-[#002c46] text-white font-semibold rounded-lg hover:bg-[#001a2a] focus:ring-2 focus:ring-[#003d5c] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={!captchaToken ? 'Veuillez valider le captcha pour continuer' : ''}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Envoi en cours...
                </>
              ) : !captchaToken ? (
                <>
                  <i className="fas fa-lock mr-2"></i>
                  Validez le captcha pour continuer
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane mr-2"></i>
                  Envoyer le lien de récupération
                </>
              )}
            </button>
            {!captchaToken && !isSubmitting && (
              <p className="text-xs text-orange-600 text-center mt-2">
                <i className="fas fa-exclamation-triangle mr-1"></i>
                Le bouton sera activé après validation du captcha
              </p>
            )}
          </form>

          {/* Retour à la connexion */}
          <div className="mt-6 space-y-3 text-center">
            <Link
              to="/verify-otp?type=recovery"
              className="block text-[#002c46] hover:text-[#001a2a] font-medium text-sm"
            >
              <i className="fas fa-key mr-2"></i>
              Vous avez déjà reçu un code ? Entrez-le ici
            </Link>
            
            <Link
              to="/login"
              className="block text-gray-500 hover:text-gray-700 font-medium text-sm"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Retour à la connexion
            </Link>
          </div>
        </div>

        {/* Support */}
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-2">
            Besoin d'aide ?
          </p>
          <Link
            to="/contact"
            className="text-[#002c46] hover:text-[#001a2a] font-medium text-sm"
          >
            <i className="fas fa-envelope mr-1"></i>
            Contactez notre support
          </Link>
        </div>
      </div>
    </div>
  );
};
